/**
 * RAMS hazards catalogue wire schemas — mobile prerequisite for the RAMS
 * wizard's hazard selection (independent RAMS input prerequisite; this is
 * INPUT surface only, not RAMS generation activation).
 *
 * Served by GET /api/mobile/v1/projects/:projectId/rams-hazards, which
 * assembles the SAME three sources the web RAMS wizard page
 * (`app/(app)/projects/[projectId]/generate/rams/page.tsx`) reads:
 *
 *   1. `library`        — active rows of the global `Hazard` catalogue with
 *                          their active `ControlBlock`s. These are REAL
 *                          library identities: the route exposes the stored
 *                          slug/name/controls verbatim and NEVER invents a
 *                          hazard, rating or control.
 *   2. `accountCustom`  — the caller's account's `AccountCustomHazard` rows,
 *                          addressed by the wizard's `custom-<id>` pseudo-slug
 *                          (HYPHEN — see `lib/hazards/account-custom-hazards.ts`
 *                          for the convention the generate route resolves).
 *   3. `projectCustom`  — the project's `ProjectCustomHazard` rows (PCPP-
 *                          extracted / support-mirrored), addressed by the
 *                          `custom_<id>` UNDERSCORE pseudo-slug. The generate
 *                          route includes every one of these UNCONDITIONALLY,
 *                          so the wire marks them `autoIncluded: true` — they
 *                          are reported separately from the selectable
 *                          library/account shapes, never merged into them.
 *
 * MISSING RATINGS STAY MISSING. `defaultRiskRating` / `riskRating` are
 * nullable and the server serialises the stored value verbatim — a hazard
 * with no rating arrives as `null` and the client must render "not rated",
 * never a synthesised default (the web page's "2x2" fallback for PDF
 * rendering is a render-time concern and deliberately NOT mirrored here).
 */
import { z } from "zod";
import { INT32_MAX, INT32_MIN } from "../common";

/** Wizard pseudo-slug prefix for an AccountCustomHazard selection (HYPHEN). */
export const RAMS_ACCOUNT_CUSTOM_HAZARD_SLUG_PREFIX = "custom-";

/** Wizard pseudo-slug prefix for a ProjectCustomHazard (UNDERSCORE). */
export const RAMS_PROJECT_CUSTOM_HAZARD_SLUG_PREFIX = "custom_";

/** One active `ControlBlock` of a library hazard. */
export const RamsHazardControlBlockSchema = z.object({
  id: z.string(),
  title: z.string(),
  controlText: z.string(),
  ppeRequired: z.string().nullable(),
  methodSteps: z.string().nullable(),
  referenceNotes: z.string().nullable(),
  orderIndex: z.number().int().min(INT32_MIN).max(INT32_MAX),
});
export type RamsHazardControlBlock = z.infer<
  typeof RamsHazardControlBlockSchema
>;

// #2411 server-boundary variant.
export const RamsHazardControlBlockStrict =
  RamsHazardControlBlockSchema.strict();

/** An active real library hazard and its active control blocks. */
export const RamsLibraryHazardSchema = z.object({
  slug: z.string(),
  category: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  /** Stored value verbatim — missing stays `null` (never synthesised). */
  defaultRiskRating: z.string().nullable(),
  whoAtRisk: z.string().nullable(),
  legislationRefs: z.string().nullable(),
  /** Active control blocks only, ordered by `orderIndex`. */
  controls: z.array(RamsHazardControlBlockSchema),
});
export type RamsLibraryHazard = z.infer<typeof RamsLibraryHazardSchema>;

// #2411 server-boundary variant.
export const RamsLibraryHazardStrict = RamsLibraryHazardSchema.strict();

/**
 * The caller's account's custom hazard, addressed by `custom-<id>`. Selectable
 * in the wizard like a library hazard (the generate route resolves the id out
 * of the pseudo-slug).
 */
export const RamsAccountCustomHazardSchema = z.object({
  slug: z
    .string()
    .startsWith(RAMS_ACCOUNT_CUSTOM_HAZARD_SLUG_PREFIX),
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  /**
   * `AccountCustomHazard.defaultRiskRating` stores the wizard's raw "LxS"
   * pick (e.g. "2x3") or a level; missing/unset arrives as `null`.
   */
  defaultRiskRating: z.string().nullable(),
  controls: z.string().nullable(),
});
export type RamsAccountCustomHazard = z.infer<
  typeof RamsAccountCustomHazardSchema
>;

// #2411 server-boundary variant.
export const RamsAccountCustomHazardStrict =
  RamsAccountCustomHazardSchema.strict();

/**
 * A project custom hazard (`custom_<id>`), reported SEPARATELY from the
 * selectable shapes because the generate route includes every one of them
 * automatically — `autoIncluded` is always `true` and the client surfaces the
 * marker rather than presenting these as picker options.
 */
export const RamsProjectCustomHazardSchema = z.object({
  slug: z
    .string()
    .startsWith(RAMS_PROJECT_CUSTOM_HAZARD_SLUG_PREFIX),
  id: z.string(),
  name: z.string(),
  description: z.string(),
  /** Stored value verbatim — missing stays `null` (never synthesised). */
  riskRating: z.string().nullable(),
  controls: z.string().nullable(),
  /** Provenance annotation (e.g. "PCPP page 13"); missing stays `null`. */
  source: z.string().nullable(),
  autoIncluded: z.literal(true),
});
export type RamsProjectCustomHazard = z.infer<
  typeof RamsProjectCustomHazardSchema
>;

// #2411 server-boundary variant.
export const RamsProjectCustomHazardStrict =
  RamsProjectCustomHazardSchema.strict();

/** GET /api/mobile/v1/projects/:projectId/rams-hazards response. */
export const RamsHazardsCatalogueResponseSchema = z.object({
  library: z.array(RamsLibraryHazardSchema),
  accountCustom: z.array(RamsAccountCustomHazardSchema),
  projectCustom: z.array(RamsProjectCustomHazardSchema),
});
export type RamsHazardsCatalogueResponse = z.infer<
  typeof RamsHazardsCatalogueResponseSchema
>;

// #2411 server-boundary variant.
export const RamsHazardsCatalogueResponseStrict =
  RamsHazardsCatalogueResponseSchema.strict();
